import {NoticeMessage} from '#/atoms/notice-message';
import {SnapGridCarousel} from '#/atoms/notice-message-carousel';
import {MessageContent} from '#/repository/types';
import {ExchangeRoute} from '#/routes/exchange/exchange-route';
import {DefaultChildren} from '#/utils/utils';
import {AnimatePresence, motion} from 'framer-motion';
import {FunctionComponent, PropsWithChildren, ReactNode} from 'react';
import {useLocation} from 'react-router';

export const ActionMenuLayout: FunctionComponent<{isLoading?: boolean} & DefaultChildren> = ({
  children,
  isLoading = false,
}) => {
  const location = useLocation();
  const isPortfolioPage = location.pathname.includes(ExchangeRoute.PORTFOLIO);

  return (
    <motion.div
      className={
        'overflow-hidden p-5 w-full h-full bg-grayblue border border-grayblue data-[portfoliopage=true]:border-deepblue shadow-lg shadow-black lg:rounded-2xl data-[loading=true]:animate-pulse'
      }
      data-loading={isLoading}
      data-portfoliopage={isPortfolioPage}
      initial={{opacity: 0, x: 25}}
      animate={{opacity: 1, x: 0}}
      exit={{opacity: 0, x: 25}}
      transition={{duration: 0.15, type: 'tween'}}
    >
      {children}
    </motion.div>
  );
};

export const ActionMenuLayoutContent: FunctionComponent<PropsWithChildren<{className?: string}>> = ({
  className = '',
  children,
}) => {
  return (
    <div className={`relative flex-1 flex flex-col items-stretch justify-between overflow-y-scroll ${className}`}>
      {children}
    </div>
  );
};

export const ActionMenuLayoutContentNamed: FunctionComponent<{
  children: {
    info?: ReactNode;
    grow: ReactNode;
    overview?: ReactNode;
  };
  noticeMessages: MessageContent[];
  className?: string;
}> = ({className = '', children, noticeMessages}) => {
  return (
    <div className={`relative flex-1 flex flex-col gap-6 items-stretch justify-between overflow-y-scroll ${className}`}>
      {children.info}
      <div className='w-full grow'>{children.grow}</div>
      <AnimatePresence>
        {!!noticeMessages.length && (
          <SnapGridCarousel>
            {noticeMessages.map((message, index) => (
              <NoticeMessage key={index} {...message}>
                {message.content}
              </NoticeMessage>
            ))}
          </SnapGridCarousel>
        )}
      </AnimatePresence>
      {children.overview}
    </div>
  );
};

export const ActionMenuLayoutFooter: FunctionComponent<
  DefaultChildren & {
    variant?: 'normal' | 'sticky';
  }
> = ({children, variant = 'sticky'}) => {
  return (
    <div className={`bg-grayblue ${variant === 'sticky' ? 'sticky bottom-0 pt-3' : ''} space-x-5 flex justify-around`}>
      {children}
    </div>
  );
};
